import {Size} from '@jetbrains/ring-ui/components/avatar/avatar'
import List from '@jetbrains/ring-ui/components/list/list'
import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'

import type {User} from '../../../services/rest'
import type {UserId} from '../../../types'
import {getAvatarSizeKey} from '../../../types'
import {emptyArray} from '../../../utils/empty'
import {resolveRelative} from '../../../utils/url'

import type {
  ChangeCommiter,
  UserSelectData,
  UserSelectItem,
  UserSelectSelectedItem,
} from './UserSelect.types'

export const SELECT_ITEM_ALL_VALUE = '__ALL__'

export const ALL_USERS_SELECT_ITEM = {
  key: '__ALL__',
  userId: null,
  label: `All users`,
  value: SELECT_ITEM_ALL_VALUE,
  rgItemType: List.ListProps.Type.ITEM,
}

export const ALL_REGISTERED_USERS_LABEL = {
  key: '__ALL_REGISTERED_USERS_LABEL__',
  userId: null,
  value: '__SEPARATOR__',
  description: 'All registered users',
  rgItemType: List.ListProps.Type.SEPARATOR,
}

export const ALL_UNREGiSTERED_USERS_LABEL = {
  key: '__ALL_UNREGiSTERED_USERS_LABEL__',
  userId: null,
  value: '__SEPARATOR__',
  description: 'All unregistered users',
  rgItemType: List.ListProps.Type.SEPARATOR,
}

export const getSelectItem = ({
  user,
  vcsUsername,
  isCurrentUser,
}: {
  user?: Partial<User>
  vcsUsername?: string
  isCurrentUser?: boolean
}): SelectItem<UserSelectItem> => {
  const {id, avatars, name} = user ?? {}
  const avatarsMap: Record<string, string | null> | null | undefined = avatars
  const avatar = avatarsMap?.[getAvatarSizeKey(Size.Size20 * 2)]
  const username = vcsUsername ?? user?.username ?? ''

  const defaultLabel = name != null && name !== '' ? `${name} (${username})` : username
  const currentUserLabel = username != null ? `Me (${username})` : 'Me'

  return {
    key: username,
    userId: id,
    username: name ?? user?.username ?? vcsUsername,
    label: (isCurrentUser ? currentUserLabel : defaultLabel) || '<anonymous>',
    value: username,
    rgItemType: List.ListProps.Type.ITEM,
    showGeneratedAvatar: id != null,
    avatar: avatar && resolveRelative(avatar),
  }
}

export const getSelectedItem = (
  items: UserSelectData,
  selected: string | null,
): UserSelectSelectedItem => items.find(item => selected === item.value) ?? null

export const getSelectedItemByUserId = (
  items: UserSelectData,
  selected: UserId | string | null,
): UserSelectSelectedItem => items.find(item => selected === item.userId) ?? null

export const getSelectedItemByUserName = (
  items: UserSelectData,
  selected: string | null,
): UserSelectSelectedItem => items.find(item => selected === item.username) ?? null

export const getCommiterUserEntity = (commiter: ChangeCommiter): User =>
  (commiter.users?.user ?? emptyArray)[0] ?? {}

export const sortCommitersByName = (
  commiters: ReadonlyArray<ChangeCommiter>,
): ReadonlyArray<ChangeCommiter> =>
  [...commiters].sort((a, b) => {
    const nameA = (getCommiterUserEntity(a).name || a.vcsUsername || '').toLowerCase()
    const nameB = (getCommiterUserEntity(b).name || b.vcsUsername || '').toLowerCase()

    if (nameA !== nameB) {
      return nameA > nameB ? 1 : -1
    }

    return 0
  })

export const sortUsersByName = (users: ReadonlyArray<User>): ReadonlyArray<User> =>
  [...users].sort((userA, userB) => {
    const nameA = (userA.name || userA.username || '').toLowerCase()
    const nameB = (userB.name || userB.username || '').toLowerCase()

    if (nameA !== nameB) {
      return nameA > nameB ? 1 : -1
    }

    return 0
  })
